import { useLazyQuery } from '@apollo/client'
import { useMemo, useCallback, useState, useRef, useEffect } from 'react'
import { useAuth } from '../../../context/auth-context'
import { LegalEntitySearchQuery, LegalEntitySearchQueryVariables } from '../../../types'
import { LEGAL_ENTITY_SEARCH } from '../Queries'

export const LEGAL_SEARCH_LIMIT = 10

export const useLegalEntitySearch = (): {
  searchTerm: string
  setSearchTerm: (value: string) => void
  handleSearch: (value: string) => void
  handleClear: () => void
  legalEntities: LegalEntitySearchQuery['legalEntitySearch']
  loading: boolean
  shouldShowOptions: boolean
} => {
  const { user } = useAuth()
  const locale = user.locale.replace('_', '-')
  const [searchTerm, setSearchTerm] = useState('')
  const [legalEntities, setLegalEntities] = useState<LegalEntitySearchQuery['legalEntitySearch']>([])
  const [isSearching, setIsSearching] = useState(false)
  const timer = useRef<number>()

  const [getLegalEntities, { data, loading }] = useLazyQuery<LegalEntitySearchQuery, LegalEntitySearchQueryVariables>(LEGAL_ENTITY_SEARCH, {
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    if (data?.legalEntitySearch && !loading) {
      setLegalEntities(data.legalEntitySearch)
      setIsSearching(false)
    }
  }, [data, loading])

  useEffect(() => {
    setLegalEntities([])
  }, [searchTerm])

  const handleSearch = useCallback(
    (value: string) => {
      const trimmedValue = value.trim()
      setSearchTerm(trimmedValue)

      // Clear existing timer
      if (timer.current) {
        clearTimeout(timer.current)
      }

      // Set loading state when starting search
      if (trimmedValue.length > 1) {
        setIsSearching(true)
      }

      // Set new timer for debounced search
      timer.current = window.setTimeout(() => {
        // Only search if there's at least one keyword with 2+ characters
        const atLeastOneKeywordWithTwoOrMoreLetters = trimmedValue.split(' ').some((keyword) => keyword.length > 1)

        if (atLeastOneKeywordWithTwoOrMoreLetters) {
          getLegalEntities({
            variables: {
              search: trimmedValue,
              limit: LEGAL_SEARCH_LIMIT,
            },
          })
        } else {
          setIsSearching(false)
        }
      }, 700)
    },
    [getLegalEntities],
  )

  const handleClear = useCallback(() => {
    setSearchTerm('')
    setLegalEntities([])
    setIsSearching(false)
    if (timer.current) {
      clearTimeout(timer.current)
    }
  }, [])

  const sortedLegalEntities = useMemo(
    () => [...legalEntities].sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id, locale)),
    [legalEntities, locale],
  )

  const shouldShowOptions = useMemo(() => {
    return searchTerm.length > 1 && sortedLegalEntities.length > 0
  }, [searchTerm, sortedLegalEntities])

  return {
    searchTerm,
    setSearchTerm,
    handleSearch,
    handleClear,
    legalEntities: sortedLegalEntities,
    loading: loading || isSearching,
    shouldShowOptions,
  }
}
